import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import DataGrid, { Column, Editing, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { FormRef, GroupItem, SimpleItem, Label } from "devextreme-react/form";
import IRecette from "../../models/IRecette";
import IClient from "../../models/IClient";
import ITypePaiementRecus from "../../models/ITypePaiementRecus";
import Guid from "../../models/Guid";
import FormSysteme from '../../components/forms/form-systeme';
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import useLookUpBase from '../../components/selectbox/LookupBase';
import useClient from '../../components/selectbox/client';
import GestionTypesPaiements from '../gestion-types-paiements/gestion-types-paiements';
import IGestionRecette from '../../models/IGestionRecette';

const typePaiementRecusTempId = new Guid("ffffffff-ffff-ffff-ffff-fffffffffffe");

export default function GestionRecettes() {
  const recetteDataSourceOption: IFormBaseDataSourceOptions<IGestionRecette> = useMemo(() => ({
    path: "recus",
    customReadByIdPath: "recus/{key}/gestion",
    customSauvegarderPath: "recus/save",
    primaryKey: "idRecu",
  }), []);
  const recetteFormDataSource = useCustomDataSource<IRecette, IGestionRecette>(recetteDataSourceOption);
  const { selectedDataRow: selectedRecette, mode, setMode, setSelectedDataRow, onFieldDataChanged, setMainForm, isFieldModifier, setIsFieldModifier } = recetteFormDataSource;
  const { PopupGestionClient, clientEditorOptions } = useClient();
  const { editorOptions: modePaiementEditorOption, PopupGestion: PopupGestionModePaiement } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "typepaiement",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des types de paiement",
    cacheKey: "lookup-typepaiement-recette-grid",
  });
  const { editorOptions: folioEditorOption } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture,
    controllerName: "folio",
    showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des folios",
    cacheKey: "lookup-folio-recette-grid",
  });
  const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);

  const formRef = useRef<FormRef>(null);
  const recettes = ["Factures"];

  const typeRecetteOptions = useMemo(() => {
    return { items: recettes }
  }, []);
  const facturesOptions = useMemo(() => {
    return { text: "Sélectionner les factures à payer", disabled: true }
  }, []);
  const montantEditorOptions = useMemo(() => {
    return {
      format: {
        type: "currency",
        precision: 2,
      },
    }
  }, []);
  const modePaiementMontantEditorOptions = useMemo(() => ({
    min: 0,
    format: "#,##0.00 $",
  }), []);
  const formatMontantPaiementCell = useCallback((e: { value: unknown }) => {
    if (e.value == null || e.value === '' || Number.isNaN(Number(e.value))) {
      return '0,00 $';
    }
    return Number(e.value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' $';
  }, []);

  const modesPaiements = selectedRecette?.modesPaiements ?? [];

  const syncModesPaiementRows = useCallback((component: { option?: (name: string) => unknown }) => {
    const rawItems = component?.option?.('dataSource');
    if (!Array.isArray(rawItems)) {
      return;
    }
    setSelectedDataRow((prev) => (prev ? { ...prev, modesPaiements: rawItems as ITypePaiementRecus[] } : prev));
    setIsFieldModifier(true);
  }, [setSelectedDataRow, setIsFieldModifier]);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  const onPopupGestionClientSelectionchanged = useCallback((selectedClient: IClient) => {
    if (mode === Mode.Lecture) return;
    setIsFieldModifier(true);
    setSelectedDataRow((prev) => (prev ? { ...prev, idClient: selectedClient.idClient! } : prev));
  }, [mode, setIsFieldModifier, setSelectedDataRow]);

  return (
    <FormListBase data={recetteFormDataSource}>
      <FormListBaseGrid dataSource={recetteFormDataSource.dataSource}
        onFocusedRowChanged={() => {
          if (mode === Mode.Modifier) setMode(Mode.Lecture)
        }}
      >
        <Column key={"grid-col-no-recu"} dataField={"noRecu"} caption="No. reçu" allowHiding={false} />
        <Column key={"grid-col-id-Client"}
          editorOptions={clientEditorOptions} dataField={"idClient"} caption="Client" allowHiding={false}>
          <Lookup {...clientEditorOptions} />
        </Column>
        <Column key={"grid-col-date-paiement"} dataField={"datePaiement"} caption="Date de paiement" allowHiding={false} />
        <Column key={"grid-col-id-mode-paiement"} dataField={"idModePaiement"} caption="Mode de paiement" allowHiding={false}>
          <Lookup {...modePaiementEditorOption} />
        </Column>
        <Column key={"grid-col-montant"} dataField={"montant"} dataType="number" caption="Montant" allowHiding={false} customizeText={formatMontantPaiementCell} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`Reçu # ${selectedRecette?.noRecu || ''} - ${clientEditorOptions.displayValue || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} effacerOptions={{ disabled: !isFieldModifier }} />
      <FormListBaseTab>
        <TabItem title="Informations">
          <PopupGestionClient isModeSelection={true} onSelectionchanged={onPopupGestionClientSelectionchanged} />
          <PopupGestionModePaiement
            onSelectionchanged={(data: any) => {
              const idTypePaiement = data?.idTypePaiement || data?.id;
              if (!idTypePaiement) return;
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idModePaiement", idTypePaiement);
            }}
          >
            <GestionTypesPaiements isModeSelection={true} />
          </PopupGestionModePaiement>
          <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedRecette || {} as IRecette} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
            <GroupItem colCount={3}>
              <GroupItem colSpan={1}>
                <SimpleItem key={"frm-id-client"} dataField={"idClient"}
                  editorType={"dxSelectBox"}
                  editorOptions={clientEditorOptions}
                >
                  <Label text={"Client"} />
                </SimpleItem>
                <SimpleItem key={"frm-type-recette"} dataField={""} editorType={"dxSelectBox"} editorOptions={typeRecetteOptions}>
                  <Label text={"Type de recette"} />
                </SimpleItem>
                <SimpleItem key={"frm-date-paiement"} dataField={"datePaiement"} editorType={"dxDateBox"}>
                  <Label text={"Date de paiement"} />
                </SimpleItem>
                <SimpleItem key={"frm-montant"} dataField={"montant"} editorOptions={montantEditorOptions}>
                  <Label text={"Paiement total"} />
                </SimpleItem>
                <SimpleItem key={"frm-no-cheque"} dataField={"noCheque"}>
                  <Label text={"Numéro de chèque"} />
                </SimpleItem>
                <SimpleItem key={"frm-reference"} dataField={"reference"}>
                  <Label text={"Référence"} />
                </SimpleItem>
                <SimpleItem key={"frm-selectionner-factures-a-payer"} editorOptions={facturesOptions} />
              </GroupItem>
              <GroupItem colSpan={2}>
                <GroupItem caption={"Modes de paiements"}>
                  <DataGrid
                    showBorders={true}
                    allowColumnResizing={true}
                    dataSource={modesPaiements}
                    keyExpr="idTypePaiementRecus"
                    filterValue={dataGridFilterValue}
                    noDataText={"Aucun mode de paiement"}
                    onInitNewRow={(e: any) => {
                      e.data.idRecu = selectedRecette?.idRecu;
                      e.data.isDeleted = false;
                      e.data.idTypePaiementRecus = typePaiementRecusTempId;
                      e.data.idTypePaiement = e.data.idTypePaiement ?? Guid.empty;
                      e.data.idFolio = e.data.idFolio ?? Guid.empty;
                      e.data.montant = e.data.montant ?? 0;
                    }}
                    onRowInserted={(e) => {
                      if (mode !== Mode.Lecture) {
                        syncModesPaiementRows(e.component);
                      }
                    }}
                    onRowUpdated={(e) => {
                      if (mode !== Mode.Lecture) {
                        syncModesPaiementRows(e.component);
                      }
                    }}
                    onRowRemoving={async (e) => {
                      e.cancel = true;
                      const index = modesPaiements.findIndex(item =>
                        item === e.data
                        || (item.idTypePaiementRecus && e.data.idTypePaiementRecus?.equals(item.idTypePaiementRecus))
                      );
                      if (index !== -1) {
                        const updatedData = [...modesPaiements];
                        updatedData[index] = { ...updatedData[index], isDeleted: true };
                        setSelectedDataRow(prev => (prev ? ({
                          ...prev,
                          modesPaiements: updatedData
                        }) : prev));
                        setIsFieldModifier(true);
                      }
                    }}
                  >
                    <Editing
                      allowAdding={mode !== Mode.Lecture}
                      allowDeleting={mode !== Mode.Lecture}
                      allowUpdating={mode !== Mode.Lecture}
                      newRowPosition={"first"}
                      mode={"cell"}
                      startEditAction={"click"}
                    />
                    <Column dataField="idTypePaiement" caption="Type de paiement" dataType="string">
                      <Lookup {...modePaiementEditorOption} />
                    </Column>
                    <Column dataField="montant" caption="Montant" dataType="number" editorOptions={modePaiementMontantEditorOptions} />
                    <Column dataField="idFolio" caption="Folio" dataType="string">
                      <Lookup {...folioEditorOption} />
                    </Column>
                    <Column dataField="isDeleted" dataType="boolean" visible={false} />
                  </DataGrid>
                </GroupItem>
              </GroupItem>
            </GroupItem>
            <GroupItem caption={"Paiements effectués pour ce reçu"}>
              <DataGrid
                showBorders={true}
                allowColumnResizing={true}
                dataSource={selectedRecette?.paiements ?? []}
                allowColumnReordering={false}
                filterValue={dataGridFilterValue}
                noDataText={""}
                onRowRemoving={async (e) => {
                  e.cancel = true;
                  const paiements = selectedRecette?.paiements ?? [];
                  const index = paiements.findIndex(item =>
                    item.idFacturationEntete?.equals(e.data.idFacturationEntete)
                  );
                  if (index !== -1) {
                    const updatedData = [...paiements];
                    updatedData[index] = { ...updatedData[index], isDeleted: true };
                    setSelectedDataRow(prev => (prev ? ({
                      ...prev,
                      paiements: updatedData
                    }) : prev));
                    setIsFieldModifier(true);
                  }
                }}
              >
                <Editing mode="cell" allowUpdating={false} allowDeleting={mode !== Mode.Lecture} allowAdding={false} newRowPosition={"first"} startEditAction={"click"} />
                <Column dataType='string' dataField='noFacture' caption={"No. facture"} />
                <Column dataType='date' dataField='dateDeVente' caption={"Date de vente"} />
                <Column dataType='date' dataField='dateEcheance' caption={"Date d'échéance"} />
                <Column dataType='number' dataField='montantFacture' caption={"Montant facture"} customizeText={formatMontantPaiementCell} />
                <Column dataType='number' dataField='dd' caption={"Paiements précédents"} customizeText={formatMontantPaiementCell} />
                <Column dataType='number' dataField='montantPaiement' caption={"Montant paiement"} customizeText={formatMontantPaiementCell} />
                <Column dataType='string' dataField='typeAuxiliaire' caption={"Mode de facture"} />
                <Column dataField="isDeleted" dataType="boolean" visible={false} />
              </DataGrid>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedRecette || {} as IRecette} /></TabItem>
      </FormListBaseTab>
    </FormListBase>
  );
}
